import { TableDG } from '@alison/material';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useGridRootProps } from '@mui/x-data-grid';
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react';
import { formatMetricValue } from '../creatives/data/format';
import { TableEmptyState } from '../creatives/TableEmptyState';
import { GRID_CONTAINER_SX, GRID_SX_BASE, pinnedTotalsRowSx } from '../creatives/utils';
import { type MetricCatalog, metricFormatFor } from './data/api';
import { CATEGORY_ICON, DEFAULT_CATEGORY_ICON } from './data/categoryVisuals';
import type { ElementsTotals, ElementTreeNode } from './data/elementsTable';
import { parentPathKeys, pathKey, TOTALS_PATH, visibleTreeRows } from './data/elementsTable';

const FIXED_POPPER_SLOT_PROPS = {
  popper: {
    strategy: 'fixed' as const,
    modifiers: [{ name: 'offset', options: { offset: [-10, -25] } }],
  },
};

const METRIC_COLUMN_WIDTH = 120;
const TOTALS_ROW_HEIGHT = 52;
const ELEMENT_FIELD = 'element';
const ELEMENT_COLUMN_MIN_WIDTH = 380;
const METRIC_FIELD_PREFIX = 'metric:';
const DEFAULT_EXPANDED_DEPTH = 2;
const BOLD_TREE_LEVELS: ElementTreeNode['level'][] = ['featureCategory', 'feature'];
const BLANK_DATA_LEVELS: ElementTreeNode['level'][] = ['category', 'featureCategory'];
const COLUMN_LEFT_PADDING_PX = 30;
const INDENT_PER_DEPTH_PX = 18;

const GRID_SX = (theme: Theme) => ({
  ...GRID_SX_BASE(theme),
  ...pinnedTotalsRowSx(theme, 'top', TOTALS_ROW_HEIGHT),
});

function isTotalsRow(row: ElementTreeNode): boolean {
  return row.path.length === 1 && row.path[0] === TOTALS_PATH[0];
}

function isBlankedLevel(row: ElementTreeNode): boolean {
  return !isTotalsRow(row) && BLANK_DATA_LEVELS.includes(row.level);
}

function CategoryTag({ name }: { name: string }): ReactNode {
  const theme = useTheme();
  const Icon = CATEGORY_ICON[name] ?? DEFAULT_CATEGORY_ICON;
  const accent = theme.palette.elementCategory[name] ?? theme.palette.elementCategory.default;
  return (
    <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          borderRadius: 1,
          color: 'primary.main',
          display: 'inline-flex',
          flexShrink: 0,
          height: 28,
          justifyContent: 'center',
          width: 28,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: alpha(accent, 0.1),
          border: '1px solid',
          borderColor: alpha(accent, 0.3),
          borderRadius: 1,
          color: accent,
          display: 'inline-flex',
          minWidth: 0,
          px: 1.5,
          py: 0.5,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Typography>
      </Box>
    </Box>
  );
}

function ElementNameCell({
  level,
  name,
  isTotalsRow,
}: {
  level: ElementTreeNode['level'];
  name: string;
  isTotalsRow: boolean;
}): ReactNode {
  if (isTotalsRow) return <span style={{ fontWeight: 700 }}>{name}</span>;
  if (level === 'category') return <CategoryTag name={name} />;
  if (level === 'tag') {
    return (
      <Box
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
          display: 'flex',
          gap: 0.75,
          minWidth: 0,
          width: '100%',
        }}
      >
        <SellOutlinedIcon sx={{ flexShrink: 0, fontSize: 14 }} />
        <Tooltip title={name} placement="bottom-start" slotProps={FIXED_POPPER_SLOT_PROPS}>
          <Box
            component="span"
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {name}
          </Box>
        </Tooltip>
      </Box>
    );
  }
  return (
    <Tooltip title={name} slotProps={FIXED_POPPER_SLOT_PROPS}>
      <Box
        component="span"
        sx={{
          display: 'block',
          fontWeight: BOLD_TREE_LEVELS.includes(level) ? 700 : 400,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Box>
    </Tooltip>
  );
}

function ElementGroupingCell({
  expanded,
  hasChildren,
  onToggle,
  row,
}: {
  expanded: boolean;
  hasChildren: boolean;
  onToggle: (key: string) => void;
  row: ElementTreeNode;
}): ReactNode {
  const rootProps = useGridRootProps();
  const ToggleIcon = expanded
    ? rootProps.slots.treeDataCollapseIcon
    : rootProps.slots.treeDataExpandIcon;

  function handleToggle(event: MouseEvent): void {
    onToggle(pathKey(row.path));
    event.stopPropagation();
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.5,
        height: '100%',
        minWidth: 0,
        pl: `${COLUMN_LEFT_PADDING_PX + (row.path.length - 1) * INDENT_PER_DEPTH_PX}px`,
      }}
    >
      {hasChildren && (
        <rootProps.slots.baseIconButton
          size="small"
          onClick={handleToggle}
          tabIndex={-1}
          {...rootProps.slotProps?.baseIconButton}
        >
          <ToggleIcon fontSize="inherit" />
        </rootProps.slots.baseIconButton>
      )}
      <ElementNameCell level={row.level} name={row.name} isTotalsRow={isTotalsRow(row)} />
    </Box>
  );
}

function metricValue(row: ElementTreeNode, metricId: string): number | null {
  // Sort value must match what renderCell shows, or blanked rows reorder invisibly.
  if (isBlankedLevel(row)) return null;
  return row.metrics.find((entry) => entry.metricId === metricId)?.value ?? null;
}

function elementColumn(
  expandedPaths: ReadonlySet<string>,
  parentKeys: ReadonlySet<string>,
  onToggle: (key: string) => void,
): GridColDef<ElementTreeNode> {
  return {
    field: ELEMENT_FIELD,
    headerName: 'Element',
    minWidth: ELEMENT_COLUMN_MIN_WIDTH,
    valueGetter: (_value, row: ElementTreeNode) => row.name,
    renderCell: (params) => (
      <ElementGroupingCell
        expanded={expandedPaths.has(pathKey(params.row.path))}
        hasChildren={parentKeys.has(pathKey(params.row.path))}
        onToggle={onToggle}
        row={params.row}
      />
    ),
  };
}

function siblingComparator(
  sortModel: GridSortModel,
): ((a: ElementTreeNode, b: ElementTreeNode) => number) | undefined {
  const entry = sortModel[0];
  if (entry === undefined || entry.sort === undefined || entry.sort === null) return undefined;
  const direction = entry.sort === 'desc' ? -1 : 1;
  if (entry.field === ELEMENT_FIELD) {
    return (a, b) => direction * a.name.localeCompare(b.name);
  }
  const metricId = entry.field.slice(METRIC_FIELD_PREFIX.length);
  return (a, b) => {
    const left = metricValue(a, metricId);
    const right = metricValue(b, metricId);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return direction * (left - right);
  };
}

function metricColumn(
  metricId: string,
  catalog: MetricCatalog | undefined,
  currencySymbol: string,
  totals: ElementsTotals | null,
): GridColDef<ElementTreeNode> {
  const metric = catalog?.[metricId];
  const format = metricFormatFor(metric);
  const totalValue =
    metric?.shape === 'ratio'
      ? null
      : (totals?.metrics.find((entry) => entry.metricId === metricId)?.value ?? null);
  return {
    field: `${METRIC_FIELD_PREFIX}${metricId}`,
    headerName: metric?.label ?? metricId.toUpperCase(),
    align: 'right',
    headerAlign: 'right',
    minWidth: METRIC_COLUMN_WIDTH,
    valueGetter: (_value, row: ElementTreeNode) => metricValue(row, metricId),
    valueFormatter: (value: number | null) => formatMetricValue(value, format, currencySymbol),
    renderCell: (params) => {
      const { row, formattedValue, value } = params;
      if (isBlankedLevel(row)) return null;
      if (isTotalsRow(row) || totalValue === null || totalValue <= 0 || typeof value !== 'number') {
        return <span>{formattedValue as string}</span>;
      }
      const percentage = `${((value / totalValue) * 100).toFixed()}%`;
      return (
        <Tooltip title={percentage}>
          <span>{formattedValue as string}</span>
        </Tooltip>
      );
    },
  };
}

export function ElementsTableGrid({
  nodes,
  totals,
  metricIds,
  catalog,
  currencySymbol,
  loading,
}: {
  nodes: ElementTreeNode[];
  totals: ElementsTotals | null;
  metricIds: string[];
  catalog: MetricCatalog | undefined;
  currencySymbol: string;
  loading: boolean;
}): ReactNode {
  const [expansionOverrides, setExpansionOverrides] = useState<Record<string, boolean>>({});
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const toggleExpansion = useCallback((key: string): void => {
    setExpansionOverrides((current) => ({
      ...current,
      [key]: current[key] === undefined ? false : !current[key],
    }));
  }, []);

  const parentKeys = useMemo(() => parentPathKeys(nodes), [nodes]);

  const expandedPaths = useMemo(() => {
    const expanded = new Set<string>();
    for (const node of nodes) {
      const key = pathKey(node.path);
      if (expansionOverrides[key] ?? node.path.length - 1 < DEFAULT_EXPANDED_DEPTH)
        expanded.add(key);
    }
    return expanded;
  }, [nodes, expansionOverrides]);

  const rows = useMemo(
    () => visibleTreeRows(nodes, expandedPaths, siblingComparator(sortModel)),
    [nodes, expandedPaths, sortModel],
  );

  const columns = useMemo<GridColDef<ElementTreeNode>[]>(
    () => [
      elementColumn(expandedPaths, parentKeys, toggleExpansion),
      ...metricIds.map((metricId) => metricColumn(metricId, catalog, currencySymbol, totals)),
    ],
    [expandedPaths, parentKeys, toggleExpansion, metricIds, catalog, currencySymbol, totals],
  );

  return (
    <Box sx={GRID_CONTAINER_SX}>
      <TableDG
        columns={columns}
        rows={rows}
        pinnedTopRow={totals && nodes.length > 0 ? totals : undefined}
        getRowId={(row: ElementTreeNode) => pathKey(row.path)}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sortingMode="server"
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        loading={loading}
        sx={GRID_SX}
        slots={{ noRowsOverlay: TableEmptyState }}
        slotProps={{
          noRowsOverlay: { children: 'No elements match the current filters.' },
        }}
      />
    </Box>
  );
}
